/**
 * `shop` — Hamyon. docs/SPEC.md §shop, faza 1.
 *
 * Prototipdan IKKI CHETLANISH (docs/SPEC.md §1):
 *
 *   X-1  Balans kartasidagi ikkinchi ko'rsatkich prototipda
 *        "Sovg'alardan tushdi · 8 430" — **diamondda**. Bu D-002 ni
 *        buzadi: sovg'a pul bermaydi, `charm` beradi. Endi "Olingan
 *        charm", rangi `--pink`.
 *   X-2  Prototipda 6 ta to'lov usuli (Payme, Uzum, Visa/MC, TON).
 *        Faqat ikkitasi qoladi (D-008). TON alohida xavf: kripto yopiq
 *        iqtisodiyotga (D-001) teskari yuza ochadi.
 *
 * Narxlar bu faylda YO'Q — hammasi `GET /config` katalogidan (qoida 22).
 */

import type { PackOut } from '@voice/api-client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { openExternal, openInvoice, tg } from '../app/telegram';
import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useBalanceRefresh, useTopup, useWallet } from '../lib/api/useWallet';
import { useConfig } from '../lib/config/useConfig';
import { fmt } from '../lib/format';
import { CTAButton, GlassCardPremium, Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

type Provider = 'stars' | 'click';

export function Shop() {
  const navigate = useNavigate();
  const config = useConfig();
  const wallet = useWallet();
  const topup = useTopup();
  const refreshBalance = useBalanceRefresh();
  const toast = useToast();

  const [packId, setPackId] = useState<number | null>(null);
  const [provider, setProvider] = useState<Provider>('stars');

  const goBack = () => navigate('/profile');
  useBackButton(true, goBack);

  const packs = config.data?.catalog.packs ?? [];
  const clickEnabled = config.data?.flags['click_enabled'] ?? false;
  // Tanlanmagan bo'lsa — birinchisi. Prototipda ham doim bittasi tanlangan.
  const selected = packs.find((p) => p.id === packId) ?? packs[0];

  async function buy(): Promise<void> {
    if (!selected) return;

    // Brauzerda `openInvoice` yo'q — tugma jim ishlamay qolmasin.
    if (!tg) {
      toast.show(uz.shop.telegramOnly);
      return;
    }

    try {
      const result = await topup.mutateAsync({
        pack_id: selected.id,
        provider,
        // Idempotentlik kaliti (qoida 6) — ikki marta bosilsa ikkinchi
        // yozuv yaratilmaydi.
        client_tx_id: crypto.randomUUID(),
      });

      if (result.checkout_url) {
        // Click — Mini App ICHIDA ochilmaydi (D-008).
        openExternal(result.checkout_url);
        return;
      }

      if (!result.invoice_link) {
        toast.show(uz.shop.failed);
        return;
      }

      toast.show(uz.shop.paying);
      openInvoice(result.invoice_link, (status) => {
        if (status === 'paid') {
          toast.show(uz.shop.paid);
          // Balans SERVERDA `successful_payment` webhookida oshadi —
          // Telegram'ning javobi bilan bir vaqtda emas.
          refreshBalance();
        } else if (status === 'cancelled') {
          toast.show(uz.shop.cancelled);
        } else {
          toast.show(uz.shop.failed);
        }
      });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : uz.common.error);
    }
  }

  if (config.isLoading || wallet.isLoading) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.loading}</div>
      </div>
    );
  }

  if (wallet.isError || !wallet.data) {
    return (
      <div className="screen">
        <div className="empty">{uz.shop.loadFailed}</div>
      </div>
    );
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="screen-bar" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="icon-btn" onClick={goBack} aria-label={uz.profile.title}>
          <Icon name="back" size={16} />
        </button>
        <h1 className="screen-bar__title">{uz.shop.title}</h1>
      </div>

      <div style={{ marginTop: 18 }}>
        <GlassCardPremium>
          <div className="balance__label">{uz.shop.balance}</div>
          <div className="balance__row">
            <Icon name="gem" size={28} />
            <div className="balance__value tnum">{fmt(wallet.data.gems)}</div>
          </div>
          <div className="balance__stats">
            <div>
              <div className="balance__stat-label">{uz.shop.spentToday}</div>
              <div className="balance__stat-value tnum">{fmt(wallet.data.spent_today)}</div>
            </div>
            <div className="balance__divider" />
            <div>
              <div className="balance__stat-label">{uz.shop.charmEarned}</div>
              <div className="balance__stat-value balance__stat-value--charm tnum">
                {fmt(wallet.data.charm)}
              </div>
            </div>
          </div>
        </GlassCardPremium>
      </div>

      <div className="section-head">
        <div className="section-head__title">{uz.shop.topup}</div>
        <div className="section-head__rule" />
      </div>

      {packs.length === 0 ? (
        <div className="empty">{uz.shop.noPacks}</div>
      ) : (
        <div className="packs">
          {packs.map((pack) => (
            <PackCard
              key={pack.id}
              pack={pack}
              active={selected?.id === pack.id}
              onPick={() => setPackId(pack.id)}
            />
          ))}
        </div>
      )}

      <div className="section-head">
        <div className="section-head__title">{uz.shop.payMethod}</div>
        <div className="section-head__rule" />
      </div>

      <div className="pay-methods">
        <PayMethod
          logo="★"
          logoMod="stars"
          name={uz.shop.stars}
          hint={uz.shop.starsHint}
          active={provider === 'stars'}
          onPick={() => setProvider('stars')}
        />
        {/* Click `click_enabled` flagi ortida — faza 4 gacha ko'rinmaydi
            (qoida 23). Ishlamaydigan tugmani ko'rsatishdan ko'ra
            ko'rsatmagan yaxshi. */}
        {clickEnabled && (
          <PayMethod
            logo="C"
            logoMod="click"
            name={uz.shop.click}
            hint={uz.shop.clickHint}
            active={provider === 'click'}
            onPick={() => setProvider('click')}
          />
        )}
      </div>

      <div style={{ marginTop: 22 }}>
        <CTAButton variant="buy" disabled={!selected || topup.isPending} onClick={() => void buy()}>
          {selected
            ? uz.shop.buy(fmt(selected.amount_gems + selected.bonus_gems))
            : uz.shop.buyEmpty}
        </CTAButton>
        <div className="legal">{uz.shop.legal}</div>
      </div>

      <div className="screen-tail screen-tail--flat" />
    </div>
  );
}

function PackCard({
  pack,
  active,
  onPick,
}: {
  pack: PackOut;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" className="pack" aria-pressed={active} onClick={onPick}>
      {pack.badge && <div className="pack__tag">{pack.badge}</div>}
      <div className="pack__amount">
        <Icon name="gem" size={16} />
        <span className="tnum">{fmt(pack.amount_gems)}</span>
      </div>
      {pack.bonus_gems > 0 && (
        <div className="pack__bonus tnum">{uz.shop.bonus(fmt(pack.bonus_gems))}</div>
      )}
      {/* Narx SERVERDAN. Stars bilan to'lanadi, shuning uchun XTR
          ko'rsatiladi — so'm narxi Click uchun (faza 4). */}
      <div className="pack__price tnum">{`${fmt(pack.price_stars)} ★`}</div>
    </button>
  );
}

function PayMethod({
  logo,
  logoMod,
  name,
  hint,
  active,
  onPick,
}: {
  logo: string;
  logoMod: 'stars' | 'click';
  name: string;
  hint: string;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" className="pay-method" aria-pressed={active} onClick={onPick}>
      <div className={`pay-method__logo pay-method__logo--${logoMod}`}>{logo}</div>
      <div className="pay-method__body">
        <div className="pay-method__name">{name}</div>
        <div className="pay-method__hint">{hint}</div>
      </div>
      <div className="pay-method__dot" />
    </button>
  );
}
